import { useEffect, useState } from "react";
import { api, formatCOP } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tag, Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";

export default function ListasPreciosPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", descripcion: "", descuento_pct: 0 });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/listas-precios");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, descuento_pct: Number(form.descuento_pct) || 0 };
      if (editing) await api.patch(`/listas-precios/${editing.id}`, payload);
      else await api.post("/listas-precios", payload);
      toast.success("Guardado");
      setOpen(false); setForm({ nombre: "", descripcion: "", descuento_pct: 0 }); setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };

  const del = async (l) => {
    if (!confirm(`Eliminar lista "${l.nombre}"?`)) return;
    try { await api.delete(`/listas-precios/${l.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Precios</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Listas de precios</h1>
          <p className="text-muted-foreground mt-2">Crea listas con descuentos automáticos y asígnalas a tus clientes.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ nombre: "", descripcion: "", descuento_pct: 0 }); setOpen(true); }} data-testid="new-lista-btn" className="bg-primary hover:bg-primary/90 h-11">
          <Plus className="w-4 h-4 mr-2" /> Nueva lista
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 py-16 text-center text-muted-foreground/70 border border-dashed border-border rounded-xl">
            <Tag className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="mt-3 text-sm">No hay listas de precios aún. La primera podría ser "Mayorista" con 15% de descuento.</div>
          </div>
        )}
        {items.map((l) => (
          <div key={l.id} className="bg-card border border-border rounded-xl p-6 card-hover" data-testid={`lista-${l.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-lg font-semibold text-foreground">{l.nombre}</div>
                {l.descripcion && <p className="text-xs text-muted-foreground mt-1">{l.descripcion}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(l); setForm({ nombre: l.nombre, descripcion: l.descripcion || "", descuento_pct: l.descuento_pct }); setOpen(true); }} className="p-2 rounded-md hover:bg-muted text-muted-foreground/70 hover:text-foreground/80" title="Editar">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => del(l)} className="p-2 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-display font-semibold text-primary">{Number(l.descuento_pct).toFixed(0)}%</span>
              <span className="text-xs text-muted-foreground">de descuento sobre precio base</span>
            </div>
            {Object.keys(l.overrides || {}).length > 0 && (
              <div className="mt-3 text-xs text-muted-foreground">{Object.keys(l.overrides).length} precio(s) personalizado(s)</div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); } }}>
        <DialogContent data-testid="lista-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar lista" : "Nueva lista"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4">
            <div>
              <Label>Nombre*</Label>
              <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="h-11 mt-1" placeholder="Ej: Mayorista, VIP, Distribuidor..." data-testid="lista-nombre" />
            </div>
            <div>
              <Label>Descuento global (%)</Label>
              <Input type="number" min="0" max="100" step="0.5" value={form.descuento_pct} onChange={(e) => setForm({ ...form, descuento_pct: e.target.value })} className="h-11 mt-1" data-testid="lista-descuento" />
            </div>
            <div>
              <Label>Descripcion</Label>
              <textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm min-h-[70px]" />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 h-11" data-testid="lista-save">{editing ? "Guardar cambios" : "Crear lista"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
